import { defineMessage } from 'react-intl';

/**
 * Field labels for the mobile `FilterSheet`, shared by every page that renders
 * one.
 *
 * **Each `defaultMessage` + `description` pair here is byte-identical to the
 * wording these strings had while they lived inside the home page's search
 * sheet.** Message ids are content hashes of exactly those two fields, so
 * rewording a `description` mints a new id and silently orphans every existing
 * translation. That also means a second page reusing these inherits the
 * translations home already has — only genuinely new copy needs Crowdin.
 *
 * `defineMessage` (not plain objects) so the formatjs build plugin injects the
 * `id`; `formatMessage` throws at runtime without one.
 */

export const FILTER_SHEET_MESSAGES = {
  locationLabel: defineMessage({
    defaultMessage: 'Location',
    description: 'Label on the mobile search sheet location field',
  }),
  locationEmpty: defineMessage({
    defaultMessage: 'All locations',
    description:
      'Default option in the mobile location SelectField when no location is picked',
  }),
  locationMany: defineMessage<{ count: number }>({
    defaultMessage: '{count} locations',
    description:
      'Summary on the mobile location SelectField when multiple locations are selected',
  }),

  languageLabel: defineMessage({
    defaultMessage: 'Language',
    description: 'Label on the mobile search sheet language field',
  }),
  languageEmpty: defineMessage({
    defaultMessage: 'All languages',
    description:
      'Default option in the mobile language SelectField when no language is picked',
  }),
  languageMany: defineMessage<{ count: number }>({
    defaultMessage: '{count} languages',
    description:
      'Summary on the mobile language SelectField when multiple languages are selected',
  }),

  categoryLabel: defineMessage({
    defaultMessage: 'Category',
    description: 'Label on the mobile search sheet category field',
  }),
  categoryEmpty: defineMessage({
    defaultMessage: 'All categories',
    description:
      'Default option in the mobile category SelectField when no category is picked',
  }),
  categoryMany: defineMessage<{ count: number }>({
    defaultMessage: '{count} categories',
    description:
      'Summary on the mobile category SelectField when multiple categories are selected',
  }),

  // New with the upcoming-events page — home has no experience filter, since
  // its cards *are* the experiences.
  experienceLabel: defineMessage({
    defaultMessage: 'Experience',
    description: 'Label on the mobile filter sheet experience field',
  }),
  experienceEmpty: defineMessage({
    defaultMessage: 'All experiences',
    description:
      'Default option in the mobile experience SelectField when no experience is picked',
  }),
  experienceMany: defineMessage<{ count: number }>({
    defaultMessage: '{count} experiences',
    description:
      'Summary on the mobile experience SelectField when multiple experiences are selected',
  }),
} as const;
